import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useCopy } from '../../lib/copyToClipboard';
import {
  DISCONNECT_COWORK_MCP_CONNECTOR,
  GET_DATA_SOURCES,
  GET_COWORK_MCP_CONNECTOR,
  REMOVE_ACCOUNT_AGENT,
  UNPAIR_WORDPRESS_SITE,
  UPDATE_DATA_SOURCE,
} from '../../graphql';
import { GetDataSourcesResponse, GetCoworkMcpConnectorResponse } from '../../types/graphql';
import type { DataSource } from '../../types';
import { showInkToast } from '../cms/InkToast';
import { AgentDialog } from './AgentDialog';
import { AddSourceDialog, ConfigureSourceDialog, WordPressDialog } from './SourceDialogs';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';
import { actionsTodayLabel, agentLine, lastActiveLabel, useAccountAgents } from './accountAgents';
import { ApiKeysSection } from './ApiKeys';
import { AgentSkipLine, AgentSkipSummary, agentCardId } from './AgentControls';
import { AgentRules } from './AgentRules';
import { FeedKeysSettings } from './FeedKeys';
import { AI_AGENTS } from '../cms/AskYourAgent';
import { APPROVALS_PATH, ASK_WORDS, GOES_OUT_WITHOUT_YOU, OUT_WORDS } from './goesOut';
import {
  hostOf,
  isTestAddress,
  onFirst,
  siteMeta,
  sitePausedNote,
  siteToggleToast,
  sourcePausedStatus,
} from './connections';

const COWORK_PARTNER_URL = 'partner://claude-cowork';
/** Agents connected after the first each have a connection here (coworkMcp.js). */
const AGENT_PARTNER_PREFIX = 'partner://agent/';

function sourceKind(type: string) {
  switch (type?.toLowerCase()) {
    case 'rss':
      return 'RSS feed';
    case 'mrss':
      return 'Media RSS';
    case 'youtube':
      return 'YouTube';
    case 'wordpress':
      return 'WordPress';
    case 'api_partner':
      return 'API partner';
    case 'browser_capture':
    case 'browser':
    case 'capture':
      return 'Browser extension';
    default:
      return type || 'Source';
  }
}

function sinceLabel(iso?: string) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `since ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

function lastActive(iso?: string | null) {
  if (!iso) return 'not used yet';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'not used yet';
  const now = new Date();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `last active ${time}`;
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `last active ${day}`;
}

function mark(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'AG').slice(0, 2).toUpperCase();
}

/** "2:35 PM" today, "Oct 24" before: when a connection was paused. */
function pausedWhen(iso?: string | null) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toDateString() === new Date().toDateString()
    ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Assistants cache the tool list when they connect, so a feature shipped
 * since stays invisible until they look again (Prototype: Agent tools
 * version, backlog 16a6bdae). The API says which version is current and which
 * this agent last loaded; "Up to date" when they match, otherwise the
 * question opens what's behind and how to refresh in the assistant. The
 * server can't make an assistant reload its tools, so there's no button
 * pretending to.
 */
function ToolsVersionNote({
  name,
  current,
  loaded,
}: {
  name: string;
  current: string | null;
  loaded: string | null;
}) {
  const [open, setOpen] = useState(false);
  const upToDate = Boolean(current && loaded && loaded === current);
  const shown = loaded || current;
  return (
    <div className="ink-conn-tools">
      <div className="ink-conn-tools-line">
        {shown ? (
          <span>
            Tools version <span className="ink-conn-tools-ver">{shown}</span>
          </span>
        ) : null}
        {upToDate ? (
          <span className="ink-conn-tools-ok">
            <span
              className="ink-dot"
              style={{ background: 'var(--ink-green)', width: 6, height: 6 }}
            />
            Up to date
          </span>
        ) : (
          <button
            type="button"
            className="ink-conn-tools-link"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            Not seeing a recent feature?
          </button>
        )}
      </div>
      {open && !upToDate ? (
        <div className="ink-conn-tools-panel">
          <p>
            {current && loaded ? (
              <>
                tinycms tools are on <span className="ink-conn-tools-ver">{current}</span>. {name}{' '}
                loaded <span className="ink-conn-tools-ver">{loaded}</span> when it last connected,
                so it doesn’t know about newer ones yet.
              </>
            ) : (
              <>
                {name} keeps a copy of what this connection can do from when it connected, so a
                newer tool can be missing until it looks again.
              </>
            )}
          </p>
          <p>Ask it to reload its tool list:</p>
          <ul>
            <li>
              <strong>Claude (claude.ai):</strong> Customize → Connectors → this connector&apos;s ⋯
              menu → Refresh tools list, then start a new chat.
            </li>
            <li>
              <strong>Claude Code:</strong> start a new session, or reconnect the server.
            </li>
            <li>
              <strong>Other assistants (such as Grok):</strong> remove and re-add the connector,
              then start a new chat.
            </li>
          </ul>
          <p className="ink-conn-tools-fine">
            Nothing else changes: your content and approvals stay as they are. Still missing after a
            refresh? Disconnect tinycms in the assistant&apos;s connectors and connect it again.
          </p>
          <button type="button" className="ink-conn-tools-link" onClick={() => setOpen(false)}>
            Close
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Which Connections dialog is open (Prototype v15). */
type Open =
  | { k: 'agent' }
  | { k: 'rotate' }
  | { k: 'source' }
  | { k: 'cfg'; source: DataSource }
  | { k: 'wp' }
  | null;

/**
 * Account → AI Agents (`part="agents"`) and Account → API & sources (the rest,
 * still at /settings/connections), UX optimization review 2026-10-24. AI Agents:
 * the connected agents (MCP address, Rotate secret, Pause, added agents) and
 * Connect an agent. API & sources (named in backlog 7654d9ef):
 * THIRD-PARTY ACCESS — Reads your content (licensed feeds and read keys, from
 * Manage › Distribution) and Writes to TinyCMS (write keys) — then WHAT YOUR
 * AGENTS READ: Sources and WordPress (which only sends its posts in). Read keys (tcf_,
 * tinycms_feed_keys) and write keys (smk_, data_source_tokens) stay separate.
 */
export function IntegrationsSettings({
  part = 'connections',
}: { part?: 'agents' | 'connections' } = {}) {
  const agentsPart = part === 'agents';
  const { copy, state: copyState } = useCopy();
  const [open, setOpen] = useState<Open>(null);
  const close = () => setOpen(null);
  // The WordPress site whose Unpair is being confirmed in its row (v18).
  const [unpairing, setUnpairing] = useState<string | null>(null);

  const { accountId, isLoading: accountsLoading } = useAccount();
  const { agents: otherAgents, refetch: refetchAgents } = useAccountAgents(accountId);
  // The agent whose Remove is being confirmed in its card or row.
  const [removing, setRemoving] = useState<string | null>(null);
  // Whether the first agent's Disconnect is being confirmed in its card.
  const [disconnecting, setDisconnecting] = useState(false);
  // Approvals' Change opens at the agent's card, once the cards are here.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (agentsPart && id && otherAgents.length) {
      document.getElementById(id)?.scrollIntoView({ block: 'center' });
    }
  }, [agentsPart, otherAgents.length]);

  const {
    data: dataSourcesData,
    loading: dataSourcesLoading,
    refetch: refetchDataSources,
  } = useQuery<GetDataSourcesResponse>(GET_DATA_SOURCES, {
    variables: { accountId },
    skip: !accountId || accountsLoading,
  });

  const {
    data: coworkData,
    loading: coworkLoading,
    refetch: refetchCowork,
  } = useQuery<GetCoworkMcpConnectorResponse>(GET_COWORK_MCP_CONNECTOR, {
    variables: { accountId },
    skip: !accountId || accountsLoading,
  });

  // #feeds and #api-keys sit above the sources, which make the page long enough
  // to bring them to the top (FeedKeysSettings scrolls too, if it loads last).
  useEffect(() => {
    if (agentsPart || dataSourcesLoading) return;
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [agentsPart, dataSourcesLoading]);

  const coworkConnector = coworkData?.coworkMcpConnector ?? null;
  // The connected agent's rules come with the agent list (accountAgents).
  const coworkAgent = coworkConnector
    ? otherAgents.find((a) => a.dataSourceId === coworkConnector.dataSourceId) || null
    : null;
  // Agents added after the first: a card each, with Remove (Prototype v15).
  // The first one's card has Disconnect instead.
  const addedAgents = otherAgents.filter(
    (a) => a.removable && a.dataSourceId !== coworkConnector?.dataSourceId
  );

  const [updateDataSource, { loading: updatingDataSource }] = useMutation(UPDATE_DATA_SOURCE, {
    onCompleted: () => {
      refetchDataSources();
      refetchCowork();
    },
    onError: (error) => {
      showInkToast(error.message || 'That didn’t work. Try again.');
    },
  });

  // Pause / Resume the agent's connection: its calls are refused at once while paused.
  const togglePause = async () => {
    if (!coworkConnector) return;
    const pausing = coworkConnector.isActive;
    const r = await updateDataSource({
      variables: { id: coworkConnector.dataSourceId, input: { isActive: !pausing } },
    });
    if (!r?.data) return;
    showInkToast(
      pausing
        ? `${coworkConnector.name} paused. Its calls are refused until you resume.`
        : `${coworkConnector.name} resumed.`
    );
  };

  const [unpairSite, { loading: unpairBusy }] = useMutation(UNPAIR_WORDPRESS_SITE, {
    onError: (error) => showInkToast(error.message || 'That didn’t work. Try again.'),
  });

  const setActive = (ids: string[], isActive: boolean) =>
    Promise.all(ids.map((id) => updateDataSource({ variables: { id, input: { isActive } } }))).then(
      (results) => results.every((r) => !r.errors)
    );

  const toggleSource = (source: DataSource) =>
    setActive([source.id], !source.isActive).then(
      (ok) =>
        ok &&
        showInkToast(
          source.isActive
            ? `${source.name} paused. Nothing new comes in until you turn it back on.`
            : `${source.name} is on again.`
        )
    );

  const toggleSite = (site: DataSource) =>
    setActive([site.id], !site.isActive).then(
      (ok) => ok && showInkToast(siteToggleToast(site, !site.isActive))
    );

  const [removeAgent, { loading: removeBusy }] = useMutation(REMOVE_ACCOUNT_AGENT, {
    refetchQueries: [GOES_OUT_WITHOUT_YOU],
    onError: (error) => showInkToast(error.message || 'That didn’t work. Try again.'),
  });

  const remove = async (id: string, name: string) => {
    const res = await removeAgent({ variables: { accountId, id } });
    setRemoving(null);
    if (!res.data?.removeAccountAgent) return;
    await Promise.all([refetchAgents(), refetchCowork()]);
    showInkToast(`${name} removed. Its secret no longer works.`);
  };

  // Disconnect the first agent (Account → AI Agents): its keys and sign-ins
  // stop working at once and the screen goes back to "No agent connected".
  // Unlike Pause there is no Resume: Connect an agent starts over.
  const [disconnectAgent, { loading: disconnectBusy }] = useMutation(
    DISCONNECT_COWORK_MCP_CONNECTOR,
    {
      refetchQueries: [GOES_OUT_WITHOUT_YOU],
      onError: (error) => showInkToast(error.message || 'That didn’t work. Try again.'),
    }
  );

  const disconnect = async () => {
    if (!coworkConnector) return;
    const name = coworkConnector.name;
    const res = await disconnectAgent({ variables: { accountId } });
    setDisconnecting(false);
    if (!res.data?.disconnectCoworkMcpConnector) return;
    await Promise.all([refetchCowork(), refetchAgents()]);
    showInkToast(`${name} disconnected. Its secret and sign-ins no longer work.`);
  };

  const unpair = async (site: DataSource) => {
    const res = await unpairSite({ variables: { id: site.id } });
    setUnpairing(null);
    if (!res.data?.unpairWordPressSite) return;
    await refetchDataSources();
    showInkToast(`${hostOf(site.url) || site.name} unpaired.`);
  };

  const feedDataSources =
    dataSourcesData?.dataSources?.filter(
      (source) =>
        !(
          source.type?.toLowerCase() === 'api_partner' &&
          (source.url === COWORK_PARTNER_URL || source.url?.startsWith(AGENT_PARTNER_PREFIX))
        )
    ) ?? [];

  const wordpressSources = feedDataSources.filter(
    (source) => source.type?.toLowerCase() === 'wordpress'
  );
  const otherSources = onFirst(
    feedDataSources.filter((source) => source.type?.toLowerCase() !== 'wordpress')
  );
  const pausedSources = otherSources.filter((source) => !source.isActive);

  // "Turn all on", with Undo pausing the same ones again (Prototype v18).
  const turnAllOn = () => {
    const ids = pausedSources.map((source) => source.id);
    setActive(ids, true).then(
      (ok) =>
        ok &&
        showInkToast(`Turned on ${ids.length} sources. New items show up as they arrive.`, {
          label: 'Undo',
          onClick: () => setActive(ids, false),
        })
    );
  };

  return (
    <div>
      {agentsPart ? (
        <>
          <h1 className="ink-h1">AI Agents</h1>
          <p className="ink-lede">
            The agents that work on this account and what each can do. New agents start on Ask me
            first for anything that goes out. Agents don’t use seats.
          </p>
          {accountId ? <AgentSkipSummary accountId={accountId} /> : null}
          {/* Said once, above the list (backlog 64f29e13). */}
          <p className="agr-top-note">
            The limits below are each agent’s own. No agent publishes on its own: on {OUT_WORDS} you
            OK that version in the chat, on {ASK_WORDS} you approve it in tinycms. “Can it do this?”
            is access; whether it asks you first is set in{' '}
            <Link to={APPROVALS_PATH}>Approvals</Link>. Agents that use a key can’t be paused, only
            disconnected: to replace a key, remove the agent and connect it again.
          </p>
          <div className="ink-conn-head ink-acct-first">
            <div>
              <h2>Connected agents</h2>
              <p className="ink-acct-head-desc">
                For AI assistants that work with you in a chat, like Claude or ChatGPT. A script or
                partner system that sends work with no chat needs a write key in{' '}
                <Link to="/settings/connections#api-keys">API &amp; sources</Link>.
              </p>
            </div>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={!accountId}
              onClick={() => setOpen({ k: 'agent' })}
            >
              Connect an agent
            </button>
          </div>
        </>
      ) : (
        <>
          <h1 className="ink-h1">API &amp; sources</h1>
          {/* WordPress only sends its posts in: tinycms never publishes into it. */}
          <p className="ink-lede">
            Keys that let other software read or write your content, the sources your agent reads
            from, and the WordPress sites that send their posts in. AI agents are in{' '}
            <Link to={AI_AGENTS}>AI Agents</Link>, Google Analytics and Search Console in{' '}
            <Link to="/content?view=manage">Website › Manage</Link>, and social networks in{' '}
            <Link to="/reach/channels">Social › Channels</Link>.
          </p>
        </>
      )}

      {agentsPart ? (
        <>
          {coworkLoading ? (
            <Loader label="Loading" className="ink-lede" />
          ) : coworkConnector ? (
            <div className="ink-conn-agent" id={agentCardId(coworkConnector.dataSourceId)}>
              <div className="ink-conn-agent-top">
                <span className="ink-conn-mark">{mark(coworkConnector.name || 'Agent')}</span>
                <div>
                  <div className="ink-conn-name">
                    {coworkConnector.name}
                    {coworkConnector.hasCredentials && coworkConnector.isActive ? (
                      <span className="ink-conn-on">
                        <span
                          className="ink-dot"
                          style={{ background: 'var(--ink-green)', width: 6, height: 6 }}
                        />
                        Connected
                      </span>
                    ) : coworkConnector.hasCredentials ? (
                      <span className="ink-conn-on" style={{ color: 'var(--ink-amber)' }}>
                        <span
                          className="ink-dot"
                          style={{ background: 'var(--ink-amber)', width: 6, height: 6 }}
                        />
                        Paused
                      </span>
                    ) : (
                      <span className="ink-conn-on" style={{ color: 'var(--ink-ink3)' }}>
                        No secret yet
                      </span>
                    )}
                  </div>
                  <div className="ink-conn-meta">
                    Remote MCP ·{' '}
                    {!coworkConnector.isActive && pausedWhen(coworkConnector.pausedAt)
                      ? `paused at ${pausedWhen(coworkConnector.pausedAt)}`
                      : lastActive(coworkConnector.lastUsedAt)}
                    {coworkConnector.isActive && coworkAgent && coworkConnector.lastUsedAt
                      ? ` · ${actionsTodayLabel(coworkAgent.actionsToday)}`
                      : ''}
                  </div>
                  <AgentSkipLine rules={coworkAgent?.rules} />
                </div>
                <div className="ink-conn-actions">
                  <button
                    type="button"
                    className="ink-btn"
                    onClick={(e) => copy('mcpUrl', coworkConnector.mcpUrl, e)}
                    aria-live="polite"
                  >
                    {copyState('mcpUrl') === 'copied'
                      ? 'Copied'
                      : copyState('mcpUrl') === 'failed'
                        ? 'Couldn’t copy'
                        : 'Copy MCP URL'}
                  </button>
                  <button
                    type="button"
                    className="ink-btn"
                    onClick={() => setOpen({ k: 'rotate' })}
                  >
                    Rotate secret
                  </button>
                  <button
                    type="button"
                    className="ink-conn-pause"
                    onClick={() => void togglePause()}
                    disabled={updatingDataSource}
                  >
                    {coworkConnector.isActive ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    type="button"
                    className="ink-bill-link"
                    style={{ color: 'var(--ink-red)' }}
                    onClick={() => setDisconnecting(true)}
                    disabled={disconnecting}
                  >
                    Disconnect
                  </button>
                </div>
              </div>
              {disconnecting ? (
                <div
                  className="ink-conn-wp-confirm"
                  role="alertdialog"
                  aria-label="Disconnect agent"
                  style={{ marginTop: 12 }}
                >
                  <span className="ink-conn-wp-confirm-text">
                    Disconnect {coworkConnector.name}? Its secret and every sign-in bound to it stop
                    working at once. Drafts it wrote stay. You can connect an agent again any time.
                  </span>
                  <button
                    type="button"
                    className="ink-quiet-link"
                    onClick={() => setDisconnecting(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ink-btn ink-btn-danger"
                    disabled={disconnectBusy}
                    onClick={() => void disconnect()}
                  >
                    {disconnectBusy ? 'Disconnecting…' : 'Disconnect agent'}
                  </button>
                </div>
              ) : null}
              {/* Paused (Prototype: Agent paused): when, what it means, and the way back. */}
              {!coworkConnector.isActive && coworkConnector.hasCredentials ? (
                <div className="ink-conn-paused-strip" role="status">
                  <span>
                    <strong>
                      {pausedWhen(coworkConnector.pausedAt)
                        ? `Paused at ${pausedWhen(coworkConnector.pausedAt)}.`
                        : 'Paused.'}
                    </strong>{' '}
                    {coworkConnector.name}&rsquo;s calls to tinycms are refused until you resume.
                    Its drafts and proposals stay where they are.
                  </span>
                  <button
                    type="button"
                    className="ink-btn ink-btn-primary"
                    onClick={() => void togglePause()}
                    disabled={updatingDataSource}
                  >
                    Resume
                  </button>
                </div>
              ) : null}
              {coworkAgent && accountId ? (
                <AgentRules
                  accountId={accountId}
                  agent={coworkAgent}
                  onChanged={() => void refetchAgents()}
                />
              ) : null}
              <ToolsVersionNote
                name={coworkConnector.name}
                current={coworkConnector.toolsVersion || null}
                loaded={coworkConnector.toolsVersionLoaded || null}
              />
            </div>
          ) : otherAgents.length ? (
            // Agents that came another way (a pairing code, a partner key): the
            // same list Team shows, so a working agent is never "not connected".
            <div className="ink-team" style={{ marginTop: 16 }}>
              {otherAgents.map((a) => (
                <div
                  className="ink-team-row"
                  key={a.id}
                  id={agentCardId(a.dataSourceId)}
                  data-paused={a.active ? undefined : 'true'}
                >
                  <span className="ink-team-avatar">{a.name.slice(0, 2).toUpperCase()}</span>
                  <div>
                    <div className="ink-team-name">{a.name}</div>
                    <div className="ink-team-email">
                      {agentLine(a)}
                      {a.keyPrefix ? ` · key ${a.keyPrefix}…` : ''}
                    </div>
                    <AgentSkipLine rules={a.rules} />
                  </div>
                  {a.removable && removing !== a.id ? (
                    <span className="ink-conn-actions">
                      <button
                        type="button"
                        className="ink-bill-link"
                        style={{ color: 'var(--ink-red)' }}
                        onClick={() => setRemoving(a.id)}
                      >
                        Remove
                      </button>
                    </span>
                  ) : (
                    <span />
                  )}
                  <span className="ink-team-since">
                    {a.lastActiveAt ? lastActiveLabel(a.lastActiveAt) : 'Not yet'}
                  </span>
                  {removing === a.id ? (
                    <div
                      className="ink-conn-wp-confirm"
                      role="alertdialog"
                      aria-label="Remove agent"
                      style={{ gridColumn: '1 / -1' }}
                    >
                      <span className="ink-conn-wp-confirm-text">
                        Remove {a.name}? Its secret stops working at once. Drafts it wrote stay.
                      </span>
                      <button
                        type="button"
                        className="ink-quiet-link"
                        onClick={() => setRemoving(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="ink-btn ink-btn-danger"
                        disabled={removeBusy}
                        onClick={() => void remove(a.id, a.name)}
                      >
                        {removeBusy ? 'Removing…' : 'Remove agent'}
                      </button>
                    </div>
                  ) : null}
                  {accountId && a.active ? (
                    <div className="agr-in-row">
                      <AgentRules
                        accountId={accountId}
                        agent={a}
                        onChanged={() => void refetchAgents()}
                      />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="ink-lede" style={{ marginTop: 16 }}>
              No agent connected.
            </p>
          )}
          {/* Key-based agents (App Prototype, "Added agent"; backlog d5eb7af6): a
              key, not a sign-in, so no Pause or Rotate. Waiting until its
              first call; Remove says what it touches. */}
          {coworkConnector
            ? addedAgents.map((a) => {
                const connected = Boolean(a.lastActiveAt);
                return (
                  <div
                    className="ink-conn-agent"
                    key={a.id}
                    id={agentCardId(a.dataSourceId)}
                    style={{ marginTop: 12 }}
                    data-screen-label="Added agent"
                  >
                    <div className="ink-conn-agent-top">
                      <span className="ink-conn-mark">{mark(a.name)}</span>
                      <div>
                        <div className="ink-conn-name">
                          {a.name}
                          {!a.active ? (
                            <span className="ink-conn-on" style={{ color: 'var(--ink-ink3)' }}>
                              Paused
                            </span>
                          ) : (
                            <span
                              className="ink-conn-on"
                              style={connected ? undefined : { color: 'var(--ink-amber)' }}
                            >
                              <span
                                className="ink-dot"
                                style={{
                                  background: connected ? 'var(--ink-green)' : 'var(--ink-amber)',
                                  width: 6,
                                  height: 6,
                                }}
                              />
                              {connected ? 'Connected' : 'Waiting to connect'}
                            </span>
                          )}
                        </div>
                        <div className="ink-conn-meta">
                          Remote MCP · {lastActive(a.lastActiveAt)}
                          {a.active && connected ? ` · ${actionsTodayLabel(a.actionsToday)}` : ''}
                          {a.keyPrefix ? (
                            <>
                              {' · key '}
                              <span className="ink-conn-key">{a.keyPrefix}…</span>
                            </>
                          ) : null}
                        </div>
                        <AgentSkipLine rules={a.rules} />
                      </div>
                      {removing === a.id ? null : (
                        <div className="ink-conn-actions">
                          <button
                            type="button"
                            className="ink-btn"
                            onClick={(e) => copy(`mcpUrl-${a.id}`, coworkConnector.mcpUrl, e)}
                            aria-live="polite"
                          >
                            {copyState(`mcpUrl-${a.id}`) === 'copied'
                              ? 'Copied'
                              : copyState(`mcpUrl-${a.id}`) === 'failed'
                                ? 'Couldn’t copy'
                                : 'Copy MCP URL'}
                          </button>
                          <button
                            type="button"
                            className="ink-bill-link"
                            style={{ color: 'var(--ink-red)' }}
                            onClick={() => setRemoving(a.id)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="ink-conn-agent-notes">
                      {removing === a.id ? (
                        <div
                          className="ink-conn-wp-confirm"
                          role="alertdialog"
                          aria-label="Remove agent"
                        >
                          <span className="ink-conn-wp-confirm-text">
                            {connected
                              ? `Remove ${a.name}? Its key stops working at once, so its calls to tinycms are refused. Drafts and proposals it made stay.`
                              : `Remove ${a.name}? It hasn’t connected yet, so nothing it made is affected. Its key stops working.`}
                          </span>
                          <button
                            type="button"
                            className="ink-quiet-link"
                            onClick={() => setRemoving(null)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="ink-btn ink-btn-danger"
                            disabled={removeBusy}
                            onClick={() => void remove(a.id, a.name)}
                          >
                            {removeBusy ? 'Removing…' : 'Remove agent'}
                          </button>
                        </div>
                      ) : a.active && !connected ? (
                        <div
                          className="ink-conn-waiting"
                          data-screen-label="Agent waiting to connect"
                        >
                          <span className="ink-conn-waiting-dot" aria-hidden />
                          <span>
                            Waiting for its first call. Paste the MCP URL and key into your app;
                            this changes to Connected when it calls tinycms.
                          </span>
                        </div>
                      ) : null}
                    </div>
                    {accountId && a.active ? (
                      <AgentRules
                        accountId={accountId}
                        agent={a}
                        onChanged={() => void refetchAgents()}
                      />
                    ) : null}
                  </div>
                );
              })
            : null}
        </>
      ) : null}
      {agentsPart ? null : (
        <>
          <div className="ink-acct-group ink-acct-first">
            <div className="ink-acct-group-label">Third-party access</div>
            <p className="ink-acct-group-desc">
              Keys for other services. Read keys pull your published articles, pages, media and
              authors through the licensed feeds and the Content API. Write keys send articles and
              social posts in.
            </p>
          </div>
          <div className="ink-conn-head" id="feeds">
            <div>
              <h2>
                Reads your content <span className="ink-acct-chip">Pro</span>
              </h2>
              <p className="ink-acct-head-desc">
                License your content to other sites and apps. Each licensee gets its own key. See
                what they take and turn a key off any time. Feeds and keys belong to this site only.
              </p>
            </div>
          </div>
          <FeedKeysSettings />
          {accountId ? <ApiKeysSection accountId={accountId} /> : null}
          <div className="ink-acct-group">
            <div className="ink-acct-group-label">What your agents read</div>
          </div>
          {/* "Sources 9 sources" and what they're for (App Prototype; backlog 7dfd4f10). */}
          <div className="ink-conn-head">
            <div>
              <h2>
                Sources{' '}
                {otherSources.length ? (
                  <span className="ink-conn-head-muted">
                    {otherSources.length} {otherSources.length === 1 ? 'source' : 'sources'}
                  </span>
                ) : null}
              </h2>
              <p className="ink-acct-head-desc">
                Where your agent reads from to draft and cite: feeds, partner feeds and what you
                capture in the browser. What they bring in is saved for your agent to cite. Nothing
                goes live until you approve it.
              </p>
            </div>
            <button
              type="button"
              className="ink-bill-link"
              disabled={!accountId}
              onClick={() => setOpen({ k: 'source' })}
            >
              Add source
            </button>
          </div>
          {dataSourcesLoading ? (
            <Loader label="Loading" className="ink-lede" />
          ) : otherSources.length === 0 ? (
            <p className="ink-lede" style={{ marginTop: 16 }}>
              No sources yet.
            </p>
          ) : (
            <>
              {pausedSources.length >= 2 ? (
                <div className="ink-conn-paused-all" role="status">
                  <span className="ink-dot" aria-hidden />
                  <span className="ink-conn-paused-all-text">
                    <b>
                      {pausedSources.length} of {otherSources.length} sources are paused.
                    </b>{' '}
                    Your agent can’t cite or draft from paused sources, and nothing new comes in
                    from them.
                  </span>
                  <button
                    type="button"
                    className="ink-btn"
                    disabled={updatingDataSource}
                    onClick={turnAllOn}
                  >
                    Turn all on
                  </button>
                </div>
              ) : null}
              <div className="ink-conn-sources">
                {otherSources.map((source) => (
                  <div
                    className="ink-conn-source"
                    key={source.id}
                    data-paused={source.isActive ? undefined : 'true'}
                  >
                    <div className="ink-conn-source-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="ink-conn-source-name">{source.name}</div>
                        <div className="ink-conn-source-meta">
                          {sourceKind(source.type)}
                          {sinceLabel(source.createdAt) ? ` · ${sinceLabel(source.createdAt)}` : ''}
                        </div>
                      </div>
                      <span className="ink-conn-pill" data-on={source.isActive}>
                        {source.isActive ? 'On' : sourcePausedStatus(source.pausedAt)}
                      </span>
                    </div>
                    <div className="ink-conn-source-actions">
                      <button
                        type="button"
                        className={source.isActive ? 'ink-btn' : 'ink-btn ink-btn-primary'}
                        disabled={updatingDataSource}
                        onClick={() => toggleSource(source)}
                      >
                        {source.isActive ? 'Pause' : 'Turn on'}
                      </button>
                      <button
                        type="button"
                        className="ink-btn ink-btn-quiet"
                        onClick={() => setOpen({ k: 'cfg', source })}
                      >
                        Configure
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="ink-conn-head">
            <h2>WordPress</h2>
            <button type="button" className="ink-bill-link" onClick={() => setOpen({ k: 'wp' })}>
              Connect a site
            </button>
          </div>
          {wordpressSources.length === 0 ? (
            <div className="ink-conn-note">
              Install the tinycms plugin, then pair it from WordPress. The posts you publish there
              come into tinycms, so your agent can read and cite them.
            </div>
          ) : (
            <div className="ink-conn-wp">
              {wordpressSources.map((site) => {
                const host = hostOf(site.url);
                const confirming = unpairing === site.id;
                const label = site.name || host || 'WordPress site';
                return (
                  <div
                    className="ink-conn-wp-row"
                    key={site.id}
                    data-paused={site.isActive ? undefined : 'true'}
                  >
                    <div className="ink-conn-wp-top">
                      <span className="ink-conn-mark" aria-hidden>
                        W
                      </span>
                      <div className="ink-conn-wp-who">
                        <div className="ink-conn-wp-name">
                          <span>{label}</span>
                          {host && host !== label ? (
                            <span className="ink-conn-wp-host">{host}</span>
                          ) : null}
                          <span className="ink-conn-pill" data-on={site.isActive}>
                            {site.isActive ? 'On' : 'Paused'}
                          </span>
                        </div>
                        <div className="ink-conn-source-meta">
                          {siteMeta(site.createdAt, site.lastUsedAt)}
                        </div>
                      </div>
                      {confirming ? null : (
                        <div className="ink-conn-actions">
                          {site.isActive ? null : (
                            <button
                              type="button"
                              className="ink-btn ink-btn-primary"
                              disabled={updatingDataSource}
                              onClick={() => toggleSite(site)}
                            >
                              Resume
                            </button>
                          )}
                          {site.isActive ? (
                            <button
                              type="button"
                              className="ink-btn"
                              disabled={updatingDataSource}
                              onClick={() => toggleSite(site)}
                            >
                              Pause
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="ink-btn ink-btn-quiet ink-conn-danger"
                            onClick={() => setUnpairing(site.id)}
                          >
                            Unpair
                          </button>
                        </div>
                      )}
                    </div>
                    {site.isActive ? null : (
                      <div className="ink-conn-wp-note">{sitePausedNote(site.pausedAt)}</div>
                    )}
                    {isTestAddress(site.url) ? (
                      <div className="ink-conn-wp-local" data-screen-label="Localhost warning">
                        <span className="ink-conn-wp-local-dot" aria-hidden>
                          ●
                        </span>
                        <span className="ink-conn-wp-local-text">
                          <b>This is a test address.</b> {host} only exists on the computer it runs
                          on. Readers can’t open links to its posts. If it was for testing, unpair
                          it.
                        </span>
                        {confirming ? null : (
                          <button
                            type="button"
                            className="ink-btn"
                            onClick={() => setUnpairing(site.id)}
                          >
                            Unpair test site
                          </button>
                        )}
                      </div>
                    ) : null}
                    {confirming ? (
                      <div className="ink-conn-wp-confirm" role="alertdialog" aria-label="Unpair">
                        <span className="ink-conn-wp-confirm-text">
                          Unpair {label}? Its posts already in tinycms stay. New ones stop coming
                          in.
                        </span>
                        <button
                          type="button"
                          className="ink-quiet-link"
                          onClick={() => setUnpairing(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="ink-btn ink-btn-danger"
                          disabled={unpairBusy}
                          onClick={() => unpair(site)}
                        >
                          Unpair site
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {accountId && (open?.k === 'agent' || open?.k === 'rotate') ? (
        <AgentDialog
          accountId={accountId}
          mode={open.k === 'agent' ? 'connect' : 'rotate'}
          currentLabel={coworkConnector?.label || coworkConnector?.name}
          onClose={close}
          onChanged={() => {
            refetchCowork();
            refetchDataSources();
            refetchAgents();
          }}
        />
      ) : null}
      {accountId && open?.k === 'source' ? (
        <AddSourceDialog
          accountId={accountId}
          onClose={close}
          onAdded={(name) => {
            close();
            refetchDataSources();
            showInkToast(`${name} added. New items show up as they arrive.`);
          }}
        />
      ) : null}
      {accountId && open?.k === 'cfg' ? (
        <ConfigureSourceDialog
          accountId={accountId}
          source={open.source}
          kind={sourceKind(open.source.type)}
          onClose={close}
          onSaved={(name) => {
            close();
            refetchDataSources();
            showInkToast(`Saved ${name}.`);
          }}
        />
      ) : null}
      {open?.k === 'wp' ? <WordPressDialog onClose={close} /> : null}
    </div>
  );
}
